import { Injectable } from '@angular/core';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { PortalEventContext } from '@app/core/models/portal-event-context';
import { EventSourceService } from '@app/core/services/event-source.service';
import { PortalDataService } from '@app/portal/services/portal-data.service';
import { RouteDataContext } from '@app/routing/models/current-route-data';
import { RouterService } from '@app/routing/router.service';
import { Cached } from '@app/utils/cached/cached';
import { CachedEventSourceConnector } from '@app/utils/cached/cached-event-source-connector.service';
import { loggerFor } from '@app/utils/logger';

@Injectable({
  providedIn: 'root',
})
export class CachedEventSourceConnectorFactory {
  private instanceMap: Map<Instance<any>, CachedEventSourceConnector<any>> = new Map();
  private log = loggerFor('CachedEventSourceConnectorFactory');

  constructor(
    private authService: AuthenticationService,
    private eventSourceService: EventSourceService,
    private routerService: RouterService,
    private portalDataService: PortalDataService
  ) {
    this.authService.onLogout$.subscribe(() => {
      this.stopInstances();
    });
  }

  create<T>({
    cached,
    calculateId,
    context,
    watchRemembered = false,
  }: Options<T>): CachedEventSourceConnector<T> {
    const normalizedContext = Array.isArray(context) ? context : [context];

    const instance = this.findInstance(cached, normalizedContext);

    if (instance != null) {
      this.log.debug(
        `Not creating new instance. There already exists a connection for the given cached with the given context. cached=${cached.id}, context=${normalizedContext}`
      );

      return instance;
    }

    this.log.debug(`Creating new instance. cached=${cached.id}, context=${normalizedContext}`);

    const connector = new CachedEventSourceConnector(
      this.eventSourceService,
      this.routerService,
      this.portalDataService,
      { cached, calculateId, context: normalizedContext, watchRemembered }
    );

    this.instanceMap.set(
      {
        cached,
        context: normalizedContext.join(';'),
      },
      connector
    );

    return connector;
  }

  private findInstance(
    cached: Cached<any>,
    context: PortalEventContext[]
  ): CachedEventSourceConnector<any> | null {
    for (const key of this.instanceMap.keys()) {
      if (key.cached === cached && key.context === context.join(';')) {
        return this.instanceMap.get(key);
      }
    }

    return null;
  }

  private stopInstances(): void {
    this.log.debug('Stopping all instances.');

    for (const instance of this.instanceMap.values()) {
      instance.stop();
    }
  }
}

interface Instance<T> {
  cached: Cached<T>;
  context: string;
}

interface Options<T> {
  cached: Cached<T>;
  calculateId?: (routeDataContext: RouteDataContext) => string;
  context: PortalEventContext | PortalEventContext[];
  watchRemembered?: boolean;
}
